import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { useCalculationDetailsQuery, useLazyCalculationDetailsQuery } from "../../../store/api";
import { getAllFields } from "./fields-slice";
import { selectAllChoices } from "./choices-slice";
import { selectAllDates } from "./dates-slice";
import { makeId, selectAllChoicesPrices, selectChoicesPricesColumns } from "./choices-prices-slice";
import { processFlightOverride } from "./flight-override";
import { useManualReload } from "./state-slice";

const selectEntriesParam = createSelector(
    // no selectAllEntries here to avoid reload on reorder
    (state) => state.calculation.entries.entities,
    (entries) =>
        Object.values(entries).map(
            ({
                id,
                identifier,
                service,
                occupancyChoices,
                childChoices,
                count,
                exchangeRate,
                additionalService,
                group,
                data,
                day,
                duration,
                time,
                durationHours,
                serviceFromDate,
                serviceToDate,
            }) => ({
                id,
                identifier,
                service,
                occupancyChoices,
                childChoices,
                count,
                exchangeRate,
                additionalService,
                group,
                data,
                day,
                duration,
                time,
                durationHours,
                serviceFromDate,
                serviceToDate,
            })
        )
);

const selectFieldParams = createSelector(
    getAllFields,
    ({
        id: calculationId,
        organization: organizationId,
        dateRanges,
        daysCount,
        participantsRange,
        marginParticipants,
        currency,
        marginTax,
        dateOverride,
        tableView,
    }) => ({
        calculation: {id: calculationId, modelId: "tourism-journey-calculation/calculation"},
        organization: {id: organizationId, modelId: "organization/organization"},
        dateRanges,
        daysCount,
        participantsRange,
        marginParticipants,
        currency,
        marginTax,
        dateOverride,
        tableView,
    })
);
const selectChoices = createSelector(selectAllChoices, (choices) =>
    choices.map(
        ({id, occupancyType, override, overrideNoFlights}) => ({
            id,
            occupancyType,
            override,
            overrideNoFlights,
        })
    )
);
const selectChoiceParams = createSelector(
    selectChoices,
    selectChoicesPricesColumns,
    selectAllChoicesPrices,
    (choices, columns, choicesPrices) =>
        choices.map(
            ({
                id,
                occupancyType,
                override,
                overrideNoFlights,
            }) => {
                let overrideData = {};

                for (const [choiceId, groupId, paxId] of columns.filter(
                    ([choiceId]) => id === choiceId
                )) {
                    const hasPax = undefined !== paxId && null !== paxId;
                    const id = makeId(choiceId, groupId, paxId);
                    const prices = choicesPrices.find(cp => cp.id === id) || {};

                    const value = Object.fromEntries(
                        Object.entries(prices).filter(([k, v]) =>
                            [
                                'retailPrice',
                                'upchargeGroupBasePrice',
                                'manualFactor',
                                'subsidyDiscount',
                                'memberPrice',
                            ].includes(k) && undefined !== v && '' !== v
                        )
                    );

                    // only one property allowed at a time (calculate it on backend-side later)
                    if ('memberPrice' in value) {
                        delete value.subsidyDiscount;
                    } else if ('subsidyDiscount' in value) {
                        delete value.memberPrice;
                    }

                    if ('retailPrice' in value) {
                        delete value.manualFactor;
                    } else if ('manualFactor' in value) {
                        delete value.retailPrice;
                    }

                    if (Object.keys(value).length) {
                        overrideData[groupId] = {
                            ...(overrideData[groupId] || {}),
                            ...(hasPax
                                ? {[paxId]: {...(overrideData[groupId]?.[paxId] || {}), ...value}}
                                : value),
                        };
                    }
                }

                return {
                    id,
                    occupancyType,
                    override,
                    overrideNoFlights,
                    overrideData,
                };
            }
        )
);
export const selectDatesParams = createSelector(
    (state) => selectAllDates(state),
    (dates) => dates.map(({date}) => date)
);
const selectCalculationDetailsParams = createSelector(
    [
        selectEntriesParam,
        selectFieldParams,
        selectChoiceParams,
        selectDatesParams,
    ],
    (entries, fields, occupancyChoices, dates) => {
        const fieldsView = {...fields};

        const tableView = fieldsView.tableView;

        delete fieldsView.tableView;

        const params = {entries, occupancyChoices, dates, ...fieldsView};
        processFlightOverride(params);

        /*
         * Load only necessary participants for collapsed view out of performance reasons.
         * If other prices are overriden they are kept
         */
        if (tableView === 'collapsed') {
            params.participantsRange = params.marginParticipants;
        }

        return params;
    }
);


export const useCalculationDetails = () => {
    return useCalculationDetailsQuery(
        useSelector(selectCalculationDetailsParams),
        {
            skip: !useManualReload(),
        }
    );

};
